import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    CalendarCheck, Clock, Trophy, ChevronRight, Loader2,
    ShieldAlert, Ticket, Zap, Lock, Unlock, Plus, Check, X,
    Phone, Sparkles, CheckCircle2, XCircle, Gift,
} from "lucide-react";
import LiveScoresTicker from "@/components/LiveScoresTicker";
import { toast } from "sonner";


// ---------- Helpers ----------
const formatDate = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        return `${dd}/${mm}/${d.getFullYear()}`;
    } catch (_) { return ""; }
};

const formatTime = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString("sw-TZ", { hour: "2-digit", minute: "2-digit" });
    } catch (_) { return ""; }
};

const verdictCode = (v) => {
    if (v === "HOME") return "1";
    if (v === "AWAY") return "2";
    if (v === "DRAW") return "X";
    return "1X";
};

const verdictLabelSw = (v, home, away) => {
    if (v === "HOME") return `Ushindi wa ${home}`;
    if (v === "AWAY") return `Ushindi wa ${away}`;
    if (v === "DRAW") return "Sare";
    return "—";
};

const fixtureId = (fx) => fx.event_id || fx.id || `${fx.home_team || fx.home_name}-${fx.away_team || fx.away_name}`;


// ---------- Left column: fixture card (no free prediction shown) ----------
const MatchCard = ({ fx, selected, onToggle, testId }) => {
    const kickoff = fx.kickoff || fx.date;
    const home = fx.home_team || fx.home_name;
    const away = fx.away_team || fx.away_name;

    return (
        <button
            type="button"
            onClick={onToggle}
            className={`text-left w-full rounded-xl border transition-all bg-paper overflow-hidden group ${
                selected
                    ? "border-electric shadow-[0_0_0_2px_rgba(230,45,45,0.35)]"
                    : "border-border hover:border-electric/50"
            }`}
            data-testid={testId}
        >
            {/* Top strip */}
            <div className={`flex items-center justify-between px-4 py-2 ${selected ? "bg-electric/20" : "bg-electric/10 group-hover:bg-electric/15"} transition-colors`}>
                <div className="flex items-center gap-2 text-electric text-xs font-semibold uppercase tracking-wider">
                    <Trophy className="h-3.5 w-3.5" />
                    <span>Mechi Ijayo</span>
                </div>
                <div className="flex items-center gap-1.5 text-white/70 text-xs">
                    <CalendarCheck className="h-3.5 w-3.5" />
                    <span className="tabular-nums">{formatDate(kickoff)}</span>
                </div>
            </div>

            {/* Body */}
            <div className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-white/80 text-sm">
                        <Clock className="h-3.5 w-3.5 text-white/40" />
                        <span className="tabular-nums font-medium">{formatTime(kickoff)}</span>
                    </div>
                    <Badge variant="outline" className="border-white/20 text-white/60 text-[10px] uppercase tracking-wider truncate max-w-[180px]">
                        {fx.league_name || fx.competition_name || "Football"}
                    </Badge>
                </div>

                <div className="text-white text-lg font-heading font-bold leading-tight">
                    {home} <span className="text-white/40 font-normal">vs</span> {away}
                </div>

                <div className={`w-full flex items-center justify-center gap-2 py-2 rounded-md text-xs font-heading font-bold uppercase tracking-wider transition-colors ${
                    selected
                        ? "bg-emerald-500 text-white"
                        : "bg-obsidian text-white/70 group-hover:bg-electric group-hover:text-white"
                }`}>
                    {selected ? (
                        <><Check className="h-4 w-4" /> Umechaguliwa</>
                    ) : (
                        <><Plus className="h-4 w-4" /> Ongeza kwenye Mkeka</>
                    )}
                </div>
            </div>
        </button>
    );
};


// ---------- Right column: blurred prediction row (pre-payment) ----------
const BlurredPickRow = ({ fx, onRemove }) => {
    const home = fx.home_team || fx.home_name;
    const away = fx.away_team || fx.away_name;
    const kickoff = fx.kickoff || fx.date;

    return (
        <div
            className="relative rounded-lg border border-border bg-obsidian p-3"
            data-testid={`slip-row-${fixtureId(fx)}`}
        >
            <button
                onClick={() => onRemove(fx)}
                className="absolute top-2 right-2 text-white/30 hover:text-red-400 z-10"
                aria-label="Ondoa"
                data-testid={`slip-remove-${fixtureId(fx)}`}
            >
                <X className="h-3.5 w-3.5" />
            </button>

            <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider text-white/40 truncate">
                    {fx.league_name || fx.competition_name || "Football"}
                </span>
                <span className="text-[10px] text-white/40 ml-auto tabular-nums pr-6">
                    {formatDate(kickoff)} · {formatTime(kickoff)}
                </span>
            </div>

            <div className="text-white text-sm font-heading font-bold mb-2 leading-snug">
                {home} <span className="text-white/40 font-normal">vs</span> {away}
            </div>

            {/* Blurred prediction placeholder */}
            <div className="relative overflow-hidden rounded-md bg-paper/60 border border-border p-3">
                <div className="filter blur-md select-none pointer-events-none">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] uppercase tracking-wider text-white/50">Utabiri</span>
                        <span className="font-heading font-black text-electric text-lg tabular-nums">██%</span>
                    </div>
                    <div className="text-white text-sm font-bold mb-1">Ushindi wa ██████████</div>
                    <div className="text-white/70 text-xs leading-relaxed">
                        ████████ ████ ████████ ██████ ████ ████████ ████████ ██████ ████ ████████ ██████ ████████.
                    </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center gap-1.5 bg-obsidian/80 backdrop-blur-sm border border-electric/40 px-3 py-1 rounded-full text-electric text-[11px] font-bold uppercase tracking-wider">
                        <Lock className="h-3 w-3" /> Fungua kwa malipo
                    </div>
                </div>
            </div>
        </div>
    );
};


// ---------- Right column: revealed prediction row (post-payment) ----------
const RevealedPickRow = ({ pred, match }) => {
    const home = match.home_team;
    const away = match.away_team;
    const kickoff = match.kickoff;
    const confidence = pred.confidence ?? 0;
    const tone = confidence >= 70 ? "text-emerald-400"
        : confidence >= 50 ? "text-yellow-300"
        : "text-orange-300";

    return (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.04] p-3" data-testid={`slip-revealed-${match.match_id}`}>
            <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider text-white/40 truncate">
                    {match.competition || "Football"}
                </span>
                <span className="text-[10px] text-white/40 ml-auto tabular-nums">
                    {formatDate(kickoff)} · {formatTime(kickoff)}
                </span>
            </div>

            <div className="text-white text-sm font-heading font-bold mb-2 leading-snug">
                {home} <span className="text-white/40 font-normal">vs</span> {away}
            </div>

            <div className="rounded-md bg-obsidian border border-emerald-500/30 p-3">
                <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-white/50">Utabiri</span>
                    <span className={`font-heading font-black text-lg tabular-nums ${tone}`} data-testid={`revealed-conf-${match.match_id}`}>
                        {confidence}%
                    </span>
                </div>
                <div className="text-white text-sm font-bold mb-1" data-testid={`revealed-pick-${match.match_id}`}>
                    {pred.winner_label_sw || verdictLabelSw(pred.predicted_winner, home, away)}
                </div>
                {pred.reasoning_sw && (
                    <div className="text-white/75 text-xs leading-relaxed mt-1" data-testid={`revealed-reason-${match.match_id}`}>
                        {pred.reasoning_sw}
                    </div>
                )}
                {pred.key_factors && pred.key_factors.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                        {pred.key_factors.slice(0, 4).map((k, i) => (
                            <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/10 text-white/70">
                                {k}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};


// ---------- Right column container ----------
const SlipPanel = ({
    selected, onRemove, onClear,
    phone, setPhone,
    submitting, canSubmit, onPay,
    paidSlip, setPaidSlip,
    freeEligible, freePreviewMax, onFreePreview,
}) => {
    const PRICE = 1000;

    // Paid slip view
    if (paidSlip) {
        const matchById = {};
        (paidSlip.matches || []).forEach((m) => { matchById[m.match_id] = m; });
        return (
            <Card className="bg-paper border-emerald-500/40 p-5 sticky top-24" data-testid="slip-panel-paid">
                <div className="flex items-center gap-2 mb-3">
                    <Unlock className="h-4 w-4 text-emerald-400" />
                    <h3 className="font-heading text-lg font-black text-white uppercase tracking-wider">
                        Utabiri Wako
                    </h3>
                    <Badge className="ml-auto bg-emerald-500/20 text-emerald-400 border-emerald-500/40 border">
                        Umefunguliwa
                    </Badge>
                </div>

                {paidSlip.combined_confidence != null && (
                    <div className="mb-4 p-3 rounded bg-emerald-500/[0.08] border border-emerald-500/30 flex items-baseline justify-between" data-testid="slip-combined-confidence">
                        <span className="text-[10px] uppercase tracking-wider text-emerald-300">Uwezekano wa jumla</span>
                        <span className="font-heading font-black text-emerald-400 text-2xl tabular-nums">
                            {paidSlip.combined_confidence}%
                        </span>
                    </div>
                )}

                <div className="space-y-2 mb-4 max-h-[520px] overflow-y-auto pr-1" data-testid="slip-revealed-list">
                    {(paidSlip.predictions || []).map((p) => (
                        <RevealedPickRow
                            key={p.match_id}
                            pred={p}
                            match={matchById[p.match_id] || {}}
                        />
                    ))}
                </div>

                <Button
                    onClick={() => setPaidSlip(null)}
                    variant="outline"
                    className="w-full border-electric/40 text-electric hover:bg-electric/10"
                    data-testid="slip-new-btn"
                >
                    Anza mkeka mpya <ChevronRight className="h-4 w-4 ml-1" />
                </Button>

                <Link
                    to="/utabiri-wangu"
                    className="block text-center text-[11px] text-white/50 hover:text-white/80 mt-2"
                >
                    Angalia historia yote →
                </Link>
            </Card>
        );
    }

    // Pre-payment blurred view
    return (
        <Card className="bg-paper border-electric/40 p-5 sticky top-24" data-testid="slip-panel">
            <div className="flex items-center gap-2 mb-3">
                <Ticket className="h-4 w-4 text-electric" />
                <h3 className="font-heading text-lg font-black text-white uppercase tracking-wider">
                    Mkeka Wako
                </h3>
                <Badge className="ml-auto bg-electric/20 text-electric border-electric/40 border font-mono" data-testid="slip-count">
                    {selected.length}/10
                </Badge>
            </div>

            {selected.length === 0 ? (
                <div className="py-10 text-center" data-testid="slip-empty">
                    <Sparkles className="h-8 w-8 text-white/20 mx-auto mb-3" />
                    <div className="text-white/60 text-sm mb-1 font-medium">Mkeka wako ni tupu</div>
                    <div className="text-white/40 text-xs">
                        Chagua mechi upande wa kushoto ili kuanza mkeka wa utabiri.
                    </div>
                </div>
            ) : (
                <>
                    <div className="space-y-2 mb-4 max-h-[420px] overflow-y-auto pr-1" data-testid="slip-list">
                        {selected.map((fx) => (
                            <BlurredPickRow
                                key={fixtureId(fx)}
                                fx={fx}
                                onRemove={onRemove}
                            />
                        ))}
                    </div>

                    <div className="border-t border-border pt-4 space-y-3">
                        <label className="block">
                            <span className="text-[11px] text-white/50 uppercase tracking-wider flex items-center gap-1.5 mb-1">
                                <Phone className="h-3 w-3" /> Namba yako ya simu
                            </span>
                            <Input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="+2557XXXXXXXX"
                                className="bg-obsidian border-border text-white"
                                data-testid="slip-phone-input"
                            />
                        </label>

                        <div className="flex items-baseline justify-between p-3 rounded bg-obsidian border border-border">
                            <span className="text-white/60 text-xs uppercase tracking-wider">Bei</span>
                            <span className="font-heading font-black text-electric text-xl tabular-nums" data-testid="slip-price">
                                TZS {PRICE.toLocaleString()}
                            </span>
                        </div>

                        <Button
                            onClick={onPay}
                            disabled={!canSubmit || submitting}
                            className="w-full bg-electric hover:bg-electric/80 text-white font-heading font-black uppercase tracking-wider"
                            data-testid="slip-pay-btn"
                        >
                            {submitting ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Inatuma...</>
                            ) : (
                                <><Lock className="h-4 w-4 mr-2" /> Lipia TZS {PRICE.toLocaleString()} Kufungua</>
                            )}
                        </Button>

                        {freeEligible && selected.length <= freePreviewMax && (
                            <button
                                onClick={onFreePreview}
                                disabled={submitting}
                                className="w-full flex items-center justify-center gap-2 rounded-md py-2.5 border border-emerald-500/40 bg-emerald-500/[0.08] text-emerald-400 hover:bg-emerald-500/[0.15] transition-colors font-heading font-bold text-xs uppercase tracking-wider disabled:opacity-50"
                                data-testid="slip-free-preview-btn"
                            >
                                <Gift className="h-4 w-4" />
                                Fungua Bure (Mara ya Kwanza)
                            </button>
                        )}
                        {freeEligible && selected.length > freePreviewMax && (
                            <div className="text-[11px] text-emerald-300/80 text-center" data-testid="slip-free-preview-hint">
                                Punguza mpaka mechi {freePreviewMax} ili ufungue bure mara ya kwanza.
                            </div>
                        )}

                        <button
                            onClick={onClear}
                            className="w-full text-[11px] text-white/40 hover:text-white/80"
                            data-testid="slip-clear-btn"
                        >
                            Futa mkeka
                        </button>
                    </div>
                </>
            )}
        </Card>
    );
};


// ---------- Main page ----------
const BettingPredictionsPage = () => {
    const [predLeagues, setPredLeagues] = useState([]);
    const [selectedLeagueId, setSelectedLeagueId] = useState("");
    const [fixtures, setFixtures] = useState([]);
    const [loading, setLoading] = useState(true);

    // Cart-like selected matches (multi-select)
    const [selected, setSelected] = useState([]);

    // Payment state
    const [phone, setPhone] = useState(() => {
        try { return localStorage.getItem("dimbani_slip_phone") || ""; } catch (_) { return ""; }
    });
    const [submitting, setSubmitting] = useState(false);
    const [paidSlip, setPaidSlip] = useState(null);

    // First-time free preview eligibility for the current phone
    const [freeEligible, setFreeEligible] = useState(false);
    const [freePreviewMax, setFreePreviewMax] = useState(3);

    useEffect(() => {
        try { if (phone) localStorage.setItem("dimbani_slip_phone", phone); } catch (_) { /* noop */ }
    }, [phone]);

    // Debounced eligibility check whenever the phone changes
    useEffect(() => {
        const p = (phone || "").trim();
        if (p.length < 6) {
            setFreeEligible(false);
            return;
        }
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const r = await axios.get(
                    `${API}/slips/preview-eligibility?phone=${encodeURIComponent(p)}`,
                    { timeout: 12000 },
                );
                if (!cancelled) {
                    setFreeEligible(!!r.data?.eligible);
                    if (r.data?.max_matches) setFreePreviewMax(r.data.max_matches);
                }
            } catch (_) {
                if (!cancelled) setFreeEligible(false);
            }
        }, 400);
        return () => { cancelled = true; clearTimeout(t); };
    }, [phone]);

    // Load leagues
    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/predictions/leagues`);
                setPredLeagues(r.data?.leagues || []);
            } catch (_) { /* non-critical */ }
        })();
    }, []);

    // Load fixtures on league change
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({ days: "7", limit: "18" });
                if (selectedLeagueId) params.append("league_id", selectedLeagueId);
                const r = await axios.get(`${API}/predictions/upcoming?${params.toString()}`, { timeout: 60000 });
                if (!cancelled) setFixtures(r.data?.fixtures || []);
            } catch (_) {
                if (!cancelled) setFixtures([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedLeagueId]);

    const isSelected = useCallback(
        (fx) => selected.some((x) => fixtureId(x) === fixtureId(fx)),
        [selected],
    );

    const toggle = (fx) => {
        setSelected((prev) => {
            const id = fixtureId(fx);
            if (prev.some((x) => fixtureId(x) === id)) {
                return prev.filter((x) => fixtureId(x) !== id);
            }
            if (prev.length >= 10) {
                toast.warning("Umefikia kikomo cha mechi 10");
                return prev;
            }
            return [...prev, fx];
        });
    };

    const removeFromSlip = (fx) => {
        const id = fixtureId(fx);
        setSelected((prev) => prev.filter((x) => fixtureId(x) !== id));
    };

    const clearSlip = () => setSelected([]);

    const canSubmit = selected.length >= 1 && phone.trim().length >= 6;

    const pay = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            // 1. Create draft slip
            const createRes = await axios.post(`${API}/slips`, {
                phone_number: phone.trim(),
                matches: selected.map((fx) => ({
                    match_id: String(fixtureId(fx)),
                    home_team: fx.home_team || fx.home_name,
                    away_team: fx.away_team || fx.away_name,
                    competition: fx.league_name || fx.competition_name || "",
                    kickoff: fx.kickoff || fx.date || "",
                    home_logo: fx.home_logo || null,
                    away_logo: fx.away_logo || null,
                })),
            }, { timeout: 25000 });

            const slip = createRes.data;

            // Auto-unlock via active subscription
            if (slip.unlocked) {
                setPaidSlip(slip);
                setSelected([]);
                toast.success("Umefunguliwa moja kwa moja kupitia mchango wako!");
                window.scrollTo({ top: 0, behavior: "smooth" });
                return;
            }

            // 2. Pay (mock)
            const payRes = await axios.post(`${API}/slips/${slip.id}/pay`, {
                phone_number: phone.trim(),
                payment_method: "mock",
            }, { timeout: 60000 });

            setPaidSlip(payRes.data);
            setSelected([]);
            toast.success("Malipo yamefanikiwa! Utabiri umefunguliwa.");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Malipo yameshindikana. Jaribu tena.");
        } finally {
            setSubmitting(false);
        }
    };

    const pickFree = async () => {
        if (!phone.trim() || selected.length === 0) return;
        if (selected.length > freePreviewMax) {
            toast.warning(`Utabiri wa bure ni mechi ${freePreviewMax} tu.`);
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/slips/free-preview`, {
                phone_number: phone.trim(),
                matches: selected.map((fx) => ({
                    match_id: String(fixtureId(fx)),
                    home_team: fx.home_team || fx.home_name,
                    away_team: fx.away_team || fx.away_name,
                    competition: fx.league_name || fx.competition_name || "",
                    kickoff: fx.kickoff || fx.date || "",
                    home_logo: fx.home_logo || null,
                    away_logo: fx.away_logo || null,
                })),
            }, { timeout: 60000 });

            setPaidSlip(res.data);
            setSelected([]);
            setFreeEligible(false);
            toast.success("Utabiri wako wa bure umefunguliwa!");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Imeshindikana kufungua utabiri wa bure.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <LiveScoresTicker />

            {/* Header */}
            <header className="border-b border-border bg-paper/90 backdrop-blur sticky top-0 z-20">
                <div className="container-main py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                        <div className="flex items-center justify-center w-9 h-9 bg-electric rounded">
                            <Zap className="h-5 w-5 text-white" strokeWidth={3} />
                        </div>
                        <span className="dimbani-wordmark text-2xl text-white">DIMBANI</span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-5 text-sm">
                        <Link to="/" className="text-white/60 hover:text-white">Nyumbani</Link>
                        <Link to="/utabiri" className="text-electric font-bold">Utabiri</Link>
                        <Link to="/utabiri-wangu" className="text-white/60 hover:text-white flex items-center gap-1">
                            <Ticket className="h-3.5 w-3.5" /> Utabiri Wangu
                        </Link>
                    </nav>
                </div>
            </header>

            <main className="container-main py-8" data-testid="betting-page">
                {/* Hero */}
                <div className="mb-6" data-testid="page-hero">
                    <h1 className="text-3xl md:text-4xl font-heading font-black text-white uppercase tracking-tight leading-tight">
                        Utabiri
                    </h1>
                    <p className="text-white/60 mt-1 text-sm max-w-2xl">
                        Chagua mechi kadhaa kutoka kushoto, jenga mkeka wako, kisha lipia bei ndogo ili kufungua utabiri kamili wa kila mechi.
                    </p>
                </div>

                {/* League filter */}
                {predLeagues.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="league-filter">
                        <button
                            onClick={() => setSelectedLeagueId("")}
                            className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${
                                !selectedLeagueId
                                    ? "bg-electric text-white"
                                    : "bg-subtle text-white/60 hover:text-white"
                            }`}
                            data-testid="league-chip-all"
                        >
                            Zote
                        </button>
                        {predLeagues.map((lg) => (
                            <button
                                key={lg.id}
                                onClick={() => setSelectedLeagueId(lg.id)}
                                className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-colors flex items-center gap-1 ${
                                    selectedLeagueId === lg.id
                                        ? "bg-electric text-white"
                                        : "bg-subtle text-white/60 hover:text-white"
                                }`}
                                data-testid={`league-chip-${lg.id}`}
                            >
                                {lg.flag && <span>{lg.flag}</span>}
                                <span>{lg.name_sw || lg.name}</span>
                            </button>
                        ))}
                    </div>
                )}

                {/* Split layout — fixture list left, mkeka right */}
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-5">
                    {/* LEFT — fixture list (multi-select) */}
                    <div className="space-y-3" data-testid="fixture-list">
                        {loading ? (
                            <div className="flex items-center justify-center py-16 text-white/40" data-testid="fixture-loading">
                                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Inapakia mechi...
                            </div>
                        ) : fixtures.length === 0 ? (
                            <Card className="bg-paper border-border p-10 text-center text-white/50 text-sm" data-testid="fixture-empty">
                                Hakuna mechi kwenye ligi hii kwa sasa. Rudi baadaye.
                            </Card>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {fixtures.map((fx) => {
                                    const id = fixtureId(fx);
                                    return (
                                        <MatchCard
                                            key={id}
                                            fx={fx}
                                            selected={isSelected(fx)}
                                            onToggle={() => toggle(fx)}
                                            testId={`fixture-card-${id}`}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* RIGHT — mkeka panel */}
                    <div className="lg:pl-2">
                        <SlipPanel
                            selected={selected}
                            onRemove={removeFromSlip}
                            onClear={clearSlip}
                            phone={phone}
                            setPhone={setPhone}
                            submitting={submitting}
                            canSubmit={canSubmit}
                            onPay={pay}
                            paidSlip={paidSlip}
                            setPaidSlip={setPaidSlip}
                            freeEligible={freeEligible}
                            freePreviewMax={freePreviewMax}
                            onFreePreview={pickFree}
                        />

                        {/* Disclaimer */}
                        <Card className="mt-4 bg-orange-400/[0.06] border-orange-400/30 p-3 flex items-start gap-2.5" data-testid="page-disclaimer">
                            <ShieldAlert className="h-4 w-4 text-orange-300 flex-shrink-0 mt-0.5" />
                            <p className="text-orange-200/90 text-[11px] leading-relaxed">
                                Utabiri huu unatokana na taarifa za umma za timu — historia, takwimu, hali ya sasa n.k. — na SI matokeo halisi. Tumia kama mwongozo tu.
                            </p>
                        </Card>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default BettingPredictionsPage;
